import type { KeyboardEvent, ReactNode } from 'react';
import { Box, useTheme } from '@mui/material';
import { useCanvasSelection } from './useCanvasSelection';
import { ElementSelectionChrome } from './ElementSelectionChrome';
import { useElementSettingsSlot } from './elementSettingsSlot';
import type { LayersRowMenuItem } from '@/common/presentation/components/layers';

/** An element whose value resolves to nothing must still leave a clickable target on the canvas. */
const EMPTY_ELEMENT_MIN_HEIGHT = 'var(--size-20, 20px)';

export interface SelectableElementProps {
  elementId: string;
  name: string;
  children: ReactNode;
  menuItems?: LayersRowMenuItem[];
  handle?: ReactNode;
  tools?: ReactNode;
  testId: string;
}

/**
 * Makes one canvas field selectable (LLAMS-610 Part 7). A no-op passthrough — `children` render
 * exactly as given, no extra wrapper markup — whenever no `EditableDocumentProvider` is mounted
 * (every route today), so this never affects the read-only preview or the PDF (which never
 * imports this module at all).
 *
 * Keyboard path: Enter selects. Once selected, `children` becomes `EditableFieldText` — an
 * interactive textbox — so this element drops its own `role="button"` (WCAG forbids nesting one
 * interactive control inside another); `ElementSettingsPanel`'s own close button and the Layers
 * panel own deselecting from there. On the block editor canvas `CanvasElementShell` passes a real
 * drag `handle`, the ⋮ `menuItems` and the inline `tools`; elsewhere the tab grip is decorative.
 */
export function SelectableElement({ elementId, name, children, menuItems, handle, tools, testId }: SelectableElementProps) {
  const { isEditable, isSelected, select } = useCanvasSelection(elementId);
  const settingsSlot = useElementSettingsSlot();
  const hostMode = useTheme().palette.mode;

  if (!isEditable) return <>{children}</>;

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    select();
  };

  if (isSelected) {
    const inPlace = settingsSlot?.renderInPlace(elementId);
    return (
      <Box
        data-testid={testId}
        data-selected="true"
        sx={{ position: 'relative', cursor: 'text', width: '100%', minHeight: EMPTY_ELEMENT_MIN_HEIGHT }}
      >
        {children}
        {inPlace ? (
          <Box
            // The sheet is paper, but this is editor chrome: it follows the host theme again. The sheet pins the design-system tokens
            // to their light values; the scope attribute lets this subtree take the host's dark ones back (see tokens.css).
            data-color-mode-scope={hostMode === 'dark' ? 'dark' : undefined}
            sx={{
              mt: 'var(--size-8, 8px)',
              p: 'var(--size-12, 12px)',
              bgcolor: 'background.paper',
              color: 'text.primary',
              border: 'var(--border-width-xs, 1px) solid',
              borderColor: 'divider',
              colorScheme: hostMode,
              '&:empty': { display: 'none' }
            }}
            data-testid={`${testId}:in-place`}
          >
            {inPlace}
          </Box>
        ) : null}
        <ElementSelectionChrome name={name} menuItems={menuItems} handle={handle} tools={tools} testId={testId} />
      </Box>
    );
  }

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={`Select ${name}`}
      onClick={(event) => {
        event.stopPropagation();
        select();
      }}
      onKeyDown={handleKeyDown}
      data-testid={testId}
      sx={{ position: 'relative', cursor: 'pointer', width: '100%', minHeight: EMPTY_ELEMENT_MIN_HEIGHT }}
    >
      {children}
    </Box>
  );
}
